import { IRequestSender, ISuperAgentRequestFactory } from '@studyportals/mb-platform-http-requests';
import { IModelDescription } from '@studyportals/domain-client/src/i-model-description';
import { IOrganisationCard, OrganisationCardClient } from '@studyportals/domain-client';
import {
	IGetFacetsRequest,
	IGetFacetsResponse,
	IGetOrganisationCardsReponse,
	IGetOrganisationsReponse,
	IRankingOrganisationSearchFilters,
	IGetRankingIdsResponse
} from './interfaces';
import { IGetRankingsResponse } from './interfaces/get-rankings-response.interface';
import { FilterType } from './enums/filter-type';

export class RankingApiClient {
	private readonly organisationCardFactory: OrganisationCardClient;

	public constructor(
		private readonly apiUriBase: string,
		private readonly requestFactory: ISuperAgentRequestFactory,
		private readonly requestSender: IRequestSender
	) {
		this.organisationCardFactory = new OrganisationCardClient('N/A', 'N/A', 1);
	}

	public async getRankings(
		filters: IRankingOrganisationSearchFilters,
		limit: number,
		offset: number
	): Promise<IGetRankingsResponse> {
		const uri = this.constructUri('rankings');
		const request = this.requestFactory.get(uri).query({ ...filters, limit, offset });

		return await this.requestSender.sendAndExtractBody<IGetRankingsResponse>(request);
	}

	public async getOrganisationCards(
		filters: IRankingOrganisationSearchFilters,
		limit: number,
		offset: number
	): Promise<IGetOrganisationCardsReponse<IOrganisationCard>> {
		const uri = this.constructUri('cards');

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		const request = this.requestFactory.get(uri).query({ ...filters, limit, offset });

		const result = await this.requestSender.sendAndExtractBody<IGetOrganisationCardsReponse<IModelDescription>>(request);

		return {
			...result,
			cards: this.organisationCardFactory.fromDescriptions([...result.cards])
		};
	}

	public async getOrganisations(
		filters: IRankingOrganisationSearchFilters,
		limit: number,
		offset: number
	): Promise<IGetOrganisationsReponse> {
		const uri = this.constructUri('organisations');

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		const request = this.requestFactory.get(uri).query({ ...filters, limit, offset });

		return await this.requestSender.sendAndExtractBody<IGetOrganisationsReponse>(request);
	}

	public async getFacets(filters: IGetFacetsRequest): Promise<IGetFacetsResponse> {
		const uri = this.constructUri('facets');
		const queryParams = this.buildQueryParams(filters);

		const url = `${uri}?${queryParams}`;
		const request = this.requestFactory.get(url);

		return await this.requestSender.sendAndExtractBody<IGetFacetsResponse>(request);
	}

	public async getRankingIds(): Promise<IGetRankingIdsResponse> {
		const uri = this.constructUri('ranking-ids');
		const request = this.requestFactory.get(uri);

		return await this.requestSender.sendAndExtractBody<IGetRankingIdsResponse>(request);
	}

	private constructUri(relativeUri: string): string {
		return this.apiUriBase + '/' + relativeUri;
	}

	private buildQueryParams(filters: IGetFacetsRequest): string {
		const queryParams: Record<string, string | string[]> = {
			[FilterType.REQUESTED_FILTERS]: this.processRequestedFilters(filters[FilterType.REQUESTED_FILTERS]),
			[FilterType.BASE_RANKING_ID]: filters[FilterType.BASE_RANKING_ID].toString(),
			[FilterType.YEAR]: filters[FilterType.YEAR].toString()
		};

		if (filters[FilterType.PRESELECTED_FILTER_TYPE]) {
			queryParams[FilterType.PRESELECTED_FILTER_TYPE] = filters[FilterType.PRESELECTED_FILTER_TYPE].toString();
		}

		if (filters[FilterType.PRESELECTED_FILTER_VALUES]) {
			queryParams[FilterType.PRESELECTED_FILTER_VALUES] = this.processPreselectedFilterValues(filters[FilterType.PRESELECTED_FILTER_VALUES]);
		}

		if (filters[FilterType.DISCIPLINE] !== undefined) {
			queryParams[FilterType.DISCIPLINE] = filters[FilterType.DISCIPLINE] === null ? 'null' : filters[FilterType.DISCIPLINE].toString();
		}

		return new URLSearchParams(queryParams as Record<string, string>).toString();
	}

	private processRequestedFilters(requestedFilters: string[]): string {
		return requestedFilters.join(',');
	}

	private processPreselectedFilterValues(preselectedFilterValues: string[]): string {
		return [...new Set(preselectedFilterValues)].join(',');
	}
}
